import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import IncidentForm from './components/IncidentForm';
import MediaUpload from './components/MediaUpload';
import RelatedIncidents from './components/RelatedIncidents';
import SuccessModal from './components/SuccessModal';

const SafetyIncidentReporting = () => {
  const navigate = useNavigate();
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [autoSaveData, setAutoSaveData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedIncidentId, setSubmittedIncidentId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load auto-saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('incident-form-autosave');
    if (savedData) {
      try {
        setAutoSaveData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading auto-saved data:', error);
      }
    }
  }, []);

  const handleAutoSave = (formData) => {
    try {
      localStorage.setItem('incident-form-autosave', JSON.stringify(formData));
    } catch (error) {
      console.error('Error auto-saving data:', error);
    }
  };

  const handleMediaUpload = (media) => {
    setUploadedMedia(media);
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    
    try {
      // Simulate API submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate incident ID
      const incidentId = `INC-${new Date()?.getFullYear()}-${String(Math.floor(Math.random() * 9999) + 1)?.padStart(4, '0')}`;
      
      // Clear auto-saved data
      localStorage.removeItem('incident-form-autosave');
      
      // Show success modal
      setSubmittedIncidentId(incidentId);
      setShowSuccessModal(true);
      
      // Reset form and media
      setUploadedMedia([]);
      setAutoSaveData(null);
      
    } catch (error) {
      console.error('Error submitting incident report:', error);
      alert('Failed to submit incident report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewReport = () => {
    setShowSuccessModal(false);
    // Navigate to incident tracking or reports page
    navigate('/training-compliance');
  };

  const handleCreateNew = () => {
    setShowSuccessModal(false);
    // Reset all form data
    setUploadedMedia([]);
    setAutoSaveData(null);
    localStorage.removeItem('incident-form-autosave');
    window.location?.reload();
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/provider-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-warning" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Safety Incident Reporting</h1>
                <p className="text-muted-foreground">
                  Document workplace incidents with comprehensive evidence and risk assessment
                </p>
              </div>
            </div>
            
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
              <button 
                onClick={() => navigate('/provider-dashboard')}
                className="hover:text-foreground transition-clinical"
              >
                Dashboard
              </button>
              <Icon name="ChevronRight" size={16} />
              <span className="text-foreground">Safety Incident Reporting</span>
            </nav>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open Incidents</p>
                  <p className="text-2xl font-bold text-foreground">12</p>
                </div>
                <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertCircle" size={20} className="text-error" />
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-2xl font-bold text-foreground">26</p>
                </div>
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={20} className="text-warning" />
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Near Misses</p>
                  <p className="text-2xl font-bold text-foreground">15</p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Eye" size={20} className="text-primary" />
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Response</p>
                  <p className="text-2xl font-bold text-foreground">4.2h</p>
                </div>
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-success" />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Form and Media */}
            <div className="lg:col-span-2 space-y-6">
              {/* Auto-save Indicator */}
              {autoSaveData && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="Save" size={16} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-primary">Draft Recovered</p>
                      <p className="text-xs text-muted-foreground">
                        Your previous work has been automatically restored
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Incident Form */}
              <IncidentForm
                onSubmit={handleFormSubmit}
                autoSaveData={autoSaveData}
                onAutoSave={handleAutoSave}
              />

              {/* Media Upload */}
              <MediaUpload
                onMediaUpload={handleMediaUpload}
                uploadedMedia={uploadedMedia}
              />
            </div>

            {/* Right Column - Related Information */}
            <div className="lg:col-span-1">
              <RelatedIncidents />
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="mt-8 bg-error/5 border border-error/20 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Phone" size={24} className="text-error" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">Emergency Contact</h3>
                <p className="text-muted-foreground mb-4">
                  For immediate medical emergencies or critical safety incidents, contact emergency services first.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" iconName="Phone" iconPosition="left">
                    Emergency: 911
                  </Button>
                  <Button variant="outline" size="sm" iconName="Phone" iconPosition="left">
                    Safety Hotline: (555) 123-4567
                  </Button>
                  <Button variant="outline" size="sm" iconName="MessageSquare" iconPosition="left">
                    Safety Chat
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        incidentId={submittedIncidentId}
        onViewReport={handleViewReport}
        onCreateNew={handleCreateNew}
      />
    </div>
  );
};

export default SafetyIncidentReporting;